import { Maths } from 'helpers/maths';
import { calculateProductByQuantityMode } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/helpers/calculateProduct';
import { calculateAppliedAcres } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/helpers/calculateAcreage';
import {
  PlanWizardFormValues,
  FixedCostValues,
  PlanProductLineItem
} from '../../../types';
import {
  mapPlanProductToApplicationsProduct,
  mergeApplicationsProductIntoPlanLineItem
} from './planProductMapping';

const FIXED_COST_KEYS: Array<keyof FixedCostValues> = [
  'landRent',
  'equipment',
  'labor',
  'repairs',
  'taxes',
  'insurance',
  'returnToMgt'
];

/**
 * FR-068: recalculates one product's Applied Acres (from its Coverage %)
 * and dependent Total Product/Cost via the same shared
 * `calculateProduct.ts`/`unitConversionHelpers.calculateProductValues`
 * dispatch Applications/Work Orders' own `recalculateProductsOnPropertiesChange`
 * uses — replacing Plans' independent `calculateAppliedArea.js` algorithm, so
 * both no longer risk silently drifting on edge-case handling (zero area,
 * rounding). `product` has no tank/carrier fields, so the shared helper
 * always dispatches through its `ratePerAcre` quantity mode. Mirrors
 * `recalculateProductsOnPropertiesChange`'s own fallback exactly: Applied
 * Acres always updates; Total Product/Cost/Rate only update when the shared
 * helper actually returns a value (it returns `null` for a rate < 0 or zero
 * applied acres), otherwise staying at their prior value via
 * `mergeApplicationsProductIntoPlanLineItem`'s own fallback.
 */
const recalculateProduct = (
  product: PlanProductLineItem,
  newAreaValue: number
): PlanProductLineItem => {
  const appliedAreaValue = calculateAppliedAcres(
    product.coveragePercent ?? 100,
    newAreaValue
  );
  const applicationsProduct = mapPlanProductToApplicationsProduct(
    product,
    true
  );
  const calculatedValues = calculateProductByQuantityMode(
    'ratePerAcre',
    appliedAreaValue,
    applicationsProduct
  );

  return mergeApplicationsProductIntoPlanLineItem(product, {
    ...applicationsProduct,
    appliedAreaValue,
    ...(calculatedValues
      ? {
          totalProduct: calculatedValues.totalProductValue,
          ratePerAcre: calculatedValues.ratePerAreaValue,
          totalCost: calculatedValues.totalCostAtTimeOfCreation || 0
        }
      : {})
  } as any);
};

/**
 * Recomputes every Total<->Per Ac pair (Total = Per Ac x area, mirroring
 * `DetailsStep`'s `handleAreaCommit`) and each product's Applied Acres from
 * its `coveragePercent`, for a new Planned Area — used when the user changes
 * Total Applied Acreage without having customized Planned Area itself.
 *
 * Products are recalculated last and isolated per-product with a try/catch:
 * `recalculateProduct` can throw for a product shape not yet fully
 * hydrated (e.g. a package unit with no `stdfactor`), and that must not
 * abort the Fixed Cost/Projected Revenue recalculation above it.
 */
const applyPlannedAreaChange = (
  values: PlanWizardFormValues,
  setFieldValue: (field: string, value: unknown) => void,
  newArea: number
): void => {
  setFieldValue('estimatedAreaValue', newArea);
  setFieldValue('area', newArea);

  FIXED_COST_KEYS.forEach(key => {
    const perAcKey = `${key}_PerAc` as keyof FixedCostValues;
    const perAcValue = values.fixedCost[perAcKey];
    setFieldValue(
      `fixedCost.${key}`,
      // FR-088: `_PerAc` fields default to `null` (blank) until the user
      // types something — an untouched rate must stay blank through an
      // area change too, not resolve to "$0.00".
      perAcValue == null ? null : Maths.multiplyRound(perAcValue, newArea, 2)
    );
  });

  setFieldValue(
    'projectedRevenue.yieldTotal',
    Maths.multiplyRound(
      Maths.multiply(
        // FR-088: these two fields default to `null`, not `0`, in a fresh
        // create-mode wizard — `Maths.multiply` needs a real number.
        values.projectedRevenue.yieldPerArea ?? 0,
        values.projectedRevenue.pricePerUnit ?? 0
      ),
      newArea,
      2
    )
  );
  setFieldValue(
    'projectedRevenue.fsaPayment',
    // FR-088: `fsaPaymentPerAc` defaults to `null` (blank) until the user
    // types something — an untouched rate must stay blank through an area
    // change too, not resolve to "$0.00".
    values.projectedRevenue.fsaPaymentPerAc == null
      ? null
      : Maths.multiplyRound(values.projectedRevenue.fsaPaymentPerAc, newArea, 2)
  );

  if (values.planProductEntries?.length) {
    setFieldValue(
      'planProductEntries',
      values.planProductEntries.map(product => {
        try {
          return recalculateProduct(product, newArea);
        } catch {
          return product;
        }
      })
    );
  }
};

export default applyPlannedAreaChange;
